import type { ComponentType, FC, ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import type { StackProps } from "@mui/material/Stack";
import type { Theme, SxProps } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { RouteObject } from "react-router";
import { NavLink, useMatch, useResolvedPath } from "react-router";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import { useAuth } from "react-oidc-context";
import { LogOutIcon, ChevronRightIcon } from "../../shared/icons";
import type { IconProps } from "../../shared/icons";
import { logUnhandled } from "../../api/logError";
import { useSidebar } from "./Sidebar";
import { EXPANDED_SIDEBAR_WIDTH, SIDEBAR_PADDING_X } from "./sidebarConstants";
import { useCurrentCustomer, useCurrentOrganisation } from "../CurrentOrganisationProvider";
import { HACK_alignIconWithLabel } from "@privacy/ui";
import { usePermission, type Permission } from "../../shared/permissions";
import { usePrivacyNavigate, type Paths } from "../../shared/usePrivacyNavigate";
import type { UserInfoCustomer, UserInfoOrganisation } from "../../api/userInfo";

export type SidebarMenuItemType = NavigationItemType | AccordionMenuType | CustomSidebarItem;

type CustomSidebarItem = { id: string; variant: "custom"; element: ReactNode };

type SidebarMenuItemProps = { item: SidebarMenuItemType };
export const SidebarMenuItem: FC<SidebarMenuItemProps> = ({ item }) => {
  if (item.variant === "navigationItem") return <NavigationItem item={item} />;

  if (item.variant === "accordionMenu") return <AccordionMenu item={item} />;

  if (item.variant === "custom") return item.element;

  return logUnhandled(item, "sidebar item");
};

/** NavigationItem */
export type HasAccessArgs = {
  hasPermission: (permission: Permission) => boolean;
  currentOrganisation: UserInfoOrganisation;
  currentCustomer: UserInfoCustomer;
};
export type NavigationItemType = {
  variant: "navigationItem";
  labelKey: TranslationKey;
  onHover?: () => void;
  onClick?: (args: HasAccessArgs) => void;
  Icon?: ComponentType<IconProps>;
  renderEndIcon?: (props: IconProps) => ReactNode;
  renderCollapsedBadge?: () => ReactNode;
  to: (paths: Paths) => string;
  route: RouteObject & { path: string };
  hasAccess: (args: HasAccessArgs) => boolean;
  useHasAccess?: () => boolean | undefined;
};

export const useGetHasAccessArgs = (): HasAccessArgs => {
  const currentOrganisation = useCurrentOrganisation();
  const currentCustomer = useCurrentCustomer();
  const { hasPermission } = usePermission();
  return { hasPermission, currentOrganisation, currentCustomer };
};

type NavigationItemProps = {
  item: NavigationItemType;
  isNested?: boolean;
  onActiveChange?: (isActive: boolean) => void;
};

const NavigationItem: FC<NavigationItemProps> = ({ item, isNested, onActiveChange }) => {
  const isActive = useIsNavigationItemActive(item.route.path);
  const { paths } = usePrivacyNavigate();
  const hasAccessArgs = useGetHasAccessArgs();
  const emphasisSx = useEmphasisSx(isActive);

  /** The Latest Ref Pattern is used, so that the parent do not have to remember to memoize onActiveChange
   *  https://epicreact.dev/the-latest-ref-pattern-in-react/ */
  const onActiveChangeRef = useRef(onActiveChange);
  useLayoutEffect(() => {
    onActiveChangeRef.current = onActiveChange;
  });
  useLayoutEffect(() => onActiveChangeRef.current?.(isActive), [isActive]);

  const { t } = useTranslation();
  const hasItemAccess = item.useHasAccess ? item.useHasAccess() : true;

  if (!item.hasAccess(hasAccessArgs) || !hasItemAccess) return null;

  const { onClick } = item;

  return (
    <SidebarItem
      component={NavLink}
      renderStartIcon={props => (item.Icon ? <item.Icon {...props} /> : null)}
      renderEndIcon={item.renderEndIcon}
      renderCollapsedBadge={item.renderCollapsedBadge}
      label={t(item.labelKey)}
      labelSx={emphasisSx}
      to={item.to(paths)}
      isNested={isNested}
      isActive={isActive}
      onHover={item.onHover}
      onClick={onClick ? () => onClick(hasAccessArgs) : undefined}
    />
  );
};
/** AccordionMenu */

type AccordionMenuType = {
  variant: "accordionMenu";
  labelKey: TranslationKey;
  Icon: ComponentType<IconProps>;
  children: NavigationItemType[];
};

type AccordionMenuProps = {
  item: AccordionMenuType;
};

const AccordionMenu: FC<AccordionMenuProps> = ({ item }) => {
  const { t } = useTranslation();
  const hasAccessArgs = useGetHasAccessArgs();

  const [activeChildren, setActiveChildren] = useState({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const accessibleChildren = item.children.filter(
    child => child.variant === "navigationItem" && child.hasAccess(hasAccessArgs)
  );

  if (accessibleChildren.length === 0) return null;

  const handleActiveChange = (path: string, isActive: boolean) => {
    if (isActive && !isMenuOpen) setIsMenuOpen(true);

    setActiveChildren(activeChildren => ({
      ...activeChildren,
      [path]: isActive,
    }));
  };

  const hasActiveChildren = Object.values(activeChildren).some(Boolean);

  return (
    <BaseAccordionMenu
      hasActiveChildren={hasActiveChildren}
      label={t(item.labelKey)}
      Icon={item.Icon}
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    >
      {accessibleChildren.map(child => (
        <NavigationItem
          key={child.route.path}
          item={child}
          isNested
          onActiveChange={isActive => handleActiveChange(child.route.path, isActive)}
        />
      ))}
    </BaseAccordionMenu>
  );
};

const useEmphasisSx = (isActive?: boolean): SxProps<Theme> => {
  const { menuItemEmphasis } = useSidebar();
  return menuItemEmphasis === "active-item-only" && !isActive ? { fontWeight: "revert" } : {};
};

/** Building blocks for constructing a SidebarMenuItem */

type BaseAccordionMenuProps = {
  hasActiveChildren?: boolean;
  isMenuOpen: boolean;
  onMenuOpenChange: (isMenuOpen: boolean) => void;
  label: string;
  Icon: ComponentType<IconProps>;
  children: ReactNode;
};

export const BaseAccordionMenu: FC<BaseAccordionMenuProps> = ({
  hasActiveChildren,
  label,
  Icon,
  isMenuOpen,
  onMenuOpenChange,
  children,
}) => {
  const { isSidebarExpanded } = useSidebar();
  const isActive = hasActiveChildren && (!isMenuOpen || !isSidebarExpanded);
  const emphasisSx = useEmphasisSx(isActive);

  return (
    <Stack>
      <SidebarItem
        isActive={isActive}
        component="button"
        label={label}
        labelSx={emphasisSx}
        renderStartIcon={props => <Icon {...props} />}
        renderEndIcon={() => (
          <ChevronRightIcon rotate={isMenuOpen ? 90 : 0} color={theme => theme.palette.text.secondary} size={14} />
        )}
        onClick={() => onMenuOpenChange(!isMenuOpen)}
      />

      <Collapse in={isSidebarExpanded && isMenuOpen} timeout={0}>
        <Stack>{children}</Stack>
      </Collapse>
    </Stack>
  );
};

type SidebarItemProps = {
  label: string;
  labelSx?: SxProps<Theme>;
  truncateLabel?: boolean;
  subLabel?: string;
  renderStartIcon?: (props: IconProps) => ReactNode;
  renderEndIcon?: (props: IconProps) => ReactNode;
  renderCollapsedBadge?: () => ReactNode;
  isNested?: boolean;
  isActive?: boolean;
  isInteractive?: boolean;
  onHover?: () => void;
};

const START_ICON_SIZE = 16;
const MIN_HEIGHT = 32;

export const SidebarItem = <RootComponent extends FC<any>, AdditionalProps>({
  label,
  labelSx,
  truncateLabel,
  subLabel,
  renderStartIcon,
  renderEndIcon,
  renderCollapsedBadge,
  isNested,
  isActive,
  isInteractive = true,
  onHover,
  ...props
}: SidebarItemProps & StackProps<RootComponent, AdditionalProps>) => {
  const { isSidebarExpanded } = useSidebar();

  let startIcon = renderStartIcon?.({
    color: theme => (isActive ? theme.palette.text.primary : theme.palette.text.secondary),
    size: START_ICON_SIZE,
  });

  if (!startIcon) startIcon = isNested ? <PlaceholderIcon size={START_ICON_SIZE} /> : null;

  const backgroundColor: SxProps<Theme> = {
    ...(isActive
      ? { bgcolor: theme => theme.palette.grey["300"] }
      : isInteractive && { "&:hover": { bgcolor: theme => theme.palette.grey["200"] } }),
  };

  const color = (theme: Theme) =>
    isActive ? theme.palette.text.primary : isNested ? theme.palette.text.tertiary : theme.palette.text.secondary;
  const paddingY = subLabel ? 1.5 : 0.5;

  return (
    <Box
      {...props}
      onMouseEnter={onHover}
      sx={{
        "all": "unset",
        "boxSizing": "border-box",
        "cursor": isInteractive ? "pointer" : "default",
        "width": theme => `calc(${EXPANDED_SIDEBAR_WIDTH} - ${theme.spacing(2)})`,
        "mx": 1,
        "mt": 0.25,
        "alignItems": subLabel ? "flex-start" : "center",
        "display": "flex",
        "minHeight": MIN_HEIGHT,
        "&:focus-visible": { outline: "2px solid", outlineColor: theme => theme.palette.primary["300"] },
        ...(isSidebarExpanded && {
          ...backgroundColor,
          px: SIDEBAR_PADDING_X,
          borderRadius: 1,
          justifyContent: "initial",
        }),
        ...props.sx,
      }}
    >
      {startIcon && (
        <Stack
          sx={{
            minWidth: 0,
            mr: 2,
            py: paddingY,
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color,
            ...(!isSidebarExpanded && { ...backgroundColor, px: SIDEBAR_PADDING_X, mr: "auto", borderRadius: 1 }),
          }}
        >
          {renderCollapsedBadge ? (
            <Box sx={{ position: "relative", display: "flex" }}>
              {startIcon}
              {!isSidebarExpanded && renderCollapsedBadge()}
            </Box>
          ) : (
            startIcon
          )}
        </Stack>
      )}

      <Stack sx={{ minWidth: truncateLabel ? 0 : undefined }}>
        <Typography
          noWrap={truncateLabel}
          sx={[
            {
              ...HACK_alignIconWithLabel,
              pt: paddingY,
              pb: subLabel ? 0 : paddingY,
              opacity: isSidebarExpanded ? 1 : 0,
              lineHeight: isNested ? 1.3 : 1,
              fontWeight: !isNested ? 600 : "revert",
              fontSize: theme => theme.typography.pxToRem(14),
              color,
            },
            ...(Array.isArray(labelSx) ? labelSx : [labelSx]),
          ]}
        >
          {label}
        </Typography>
        {subLabel && (
          <Typography
            sx={{
              mt: 0.5,
              lineHeight: 1,
              opacity: isSidebarExpanded ? 1 : 0,
              fontSize: theme => theme.typography.pxToRem(12),
              color: theme => theme.palette.text.tertiary,
              pb: paddingY,
            }}
          >
            {subLabel}
          </Typography>
        )}
      </Stack>
      <Box sx={{ ml: "auto", pl: 1, display: "flex", py: paddingY, alignItems: "center" }}>
        {isSidebarExpanded && renderEndIcon
          ? renderEndIcon({ color: theme => theme.palette.text.secondary, size: 14 })
          : null}
      </Box>
    </Box>
  );
};

const PlaceholderIcon = ({ size }: { size: number }) => (
  <Box sx={{ width: size, height: size, visibility: "hidden" }} />
);

const useIsNavigationItemActive = (path: string) => {
  const currentOrganisation = useCurrentOrganisation();
  const resolved = useResolvedPath(`/${currentOrganisation.slug}/${path}${path.endsWith("*") ? "" : "/*"}`);

  const match = useMatch({ path: resolved.pathname });
  return Boolean(match);
};

export const LogoutSidebarItem: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();

  return (
    <SidebarItem
      label={t("Sidebar.Logout")}
      renderStartIcon={props => <LogOutIcon {...props} />}
      onClick={() => auth.signoutRedirect()}
    />
  );
};
